import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { authService } from "../services/authService";
import Logo from "../components/Logo";
import { formatApiError } from "../lib/laravelError";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const metaQuery = useQuery({
    queryKey: ["reset-password", "meta", token],
    queryFn: () => authService.getResetPasswordMeta(token),
    enabled: Boolean(token),
    retry: false,
  });

  useEffect(() => {
    const em = metaQuery.data?.email;
    if (em != null && em !== "") {
      setEmail(String(em));
    }
  }, [metaQuery.data]);

  const resetMutation = useMutation({
    mutationFn: (body) => authService.resetPassword(body),
    onSuccess: (data) => {
      toast.success(
        data?.message || "Contraseña actualizada. Ya puedes entrar con la nueva clave."
      );
      navigate("/auth", { replace: true });
    },
    onError: (err) => {
      toast.error(err?.message || "No se pudo restablecer la contraseña");
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    if (!email?.trim() || !password) {
      toast.error("Completa email y contraseña");
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    resetMutation.mutate({
      token,
      email: email.trim(),
      password,
      password_confirmation: passwordConfirmation,
    });
  }

  if (metaQuery.isError) {
    return (
      <div className="space-y-8 text-center" data-testid="reset-password-error">
        <Logo size={32} />
        <p className="text-sm text-[rgba(255,255,255,0.75)]">
          {formatApiError(metaQuery.error, "Enlace no válido o caducado.")}
        </p>
        <Link
          to="/forgot-password"
          className="text-xs uppercase tracking-[0.2em] text-[#e9b5c5] hover:text-[#f5f5f5]"
        >
          Solicitar nuevo enlace
        </Link>
      </div>
    );
  }

  if (metaQuery.isLoading || !token) {
    return (
      <p className="text-center text-xs uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
        Cargando…
      </p>
    );
  }

  return (
    <div className="space-y-10" data-testid="reset-password-page">
      <header className="flex flex-col items-center gap-6 text-center">
        <Logo size={32} />
        <div className="space-y-2">
          <p className="zx-label">Nueva clave</p>
          <h1 className="font-serif text-4xl tracking-tight md:text-5xl">Renueva tu acceso</h1>
          <p className="text-sm text-[rgba(255,255,255,0.65)]">
            Elige una contraseña nueva y confírmala.
          </p>
        </div>
      </header>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="space-y-1">
          <label className="zx-label" htmlFor="rp-email">
            Email
          </label>
          <input
            id="rp-email"
            type="email"
            className="zx-input"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="reset-password-email"
          />
        </div>
        <div className="space-y-1">
          <label className="zx-label" htmlFor="rp-pass">
            Nueva contraseña
          </label>
          <input
            id="rp-pass"
            type="password"
            autoComplete="new-password"
            className="zx-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-pass"
          />
        </div>
        <div className="space-y-1">
          <label className="zx-label" htmlFor="rp-pass2">
            Confirmar contraseña
          </label>
          <input
            id="rp-pass2"
            type="password"
            autoComplete="new-password"
            className="zx-input"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            data-testid="reset-password-pass2"
          />
        </div>
        <button
          type="submit"
          className="zx-btn-primary"
          disabled={resetMutation.isPending}
          data-testid="reset-password-submit"
        >
          {resetMutation.isPending ? "Guardando…" : "Guardar contraseña"}
        </button>
      </form>

      <div className="text-center">
        <Link
          to="/auth"
          className="text-xs uppercase tracking-[0.2em] text-[rgba(255,255,255,0.55)] hover:text-[#f5f5f5]"
        >
          ← Volver al acceso
        </Link>
      </div>
    </div>
  );
}
